import React, { useState, useEffect } from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Box from '@material-ui/core/Box';
// import Typography from '@material-ui/core/Typography';
import { useDataProvider, GetListResult, useNotify } from 'react-admin';
import { ContractorDto, JobPhaseBumpDto, PhaseTypeEnum } from '@vatos-pas/common';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { BUMP_PHASES } from '../../constants/phaseConfigurations';
import { PHASE_CONTRACTOR_LIST_LIMIT } from 'utils/phaseUtils';

/**
 * Convert phase type to contractor filter
 */
const getContractorFilter = (
  phaseType: PhaseTypeEnum,
  regionId: string,
  job: any,
) => {
  const filter: any = {
    active: true,
    suspended: false,
    archived: false,
    'contractorRegions.regionId': regionId,
  };

  // Map phase types to contractor resource types
  switch (phaseType) {
    case PhaseTypeEnum.Garage:
    case PhaseTypeEnum.Hanging:
    case PhaseTypeEnum.Hang:
      filter.hangerAvailable = true;
      break;
    case PhaseTypeEnum.Finishing:
    case PhaseTypeEnum.Finish:
      filter.finisherAvailable = true;
      break;
    case PhaseTypeEnum.Spraying:
      filter.sprayerAvailable = true;
      break;
    case PhaseTypeEnum.Prime:
    case PhaseTypeEnum.InteriorPaint:
    case PhaseTypeEnum.FinalPaint:
    case PhaseTypeEnum.CMBluetape:
    case PhaseTypeEnum.FinalBluetape:
      filter.paintAvailable = true;
      break;
    case PhaseTypeEnum.Trim1:
    case PhaseTypeEnum.Trim2:
      filter.trimAvailable = true;
      break;
    case PhaseTypeEnum.Bump1:
    case PhaseTypeEnum.Bump2:
    case PhaseTypeEnum.Bump3:
    case PhaseTypeEnum.Bump4:
    case PhaseTypeEnum.Bump5:
    case PhaseTypeEnum.DrywallTouchUp1:
    case PhaseTypeEnum.DrywallTouchUp2:
    case PhaseTypeEnum.DrywallTouchUp3:
      filter.bumperAvailable = true;
      break;
  }

  // High ceiling requirements
  if (
    (phaseType === PhaseTypeEnum.Hanging ||
      phaseType === PhaseTypeEnum.Hang ||
      phaseType === PhaseTypeEnum.Finishing ||
      phaseType === PhaseTypeEnum.Finish) &&
    job.job?.highCeiling
  ) {
    filter.canPerformHighCeilingTasks = true;
  }

  return filter;
};

export const ContractorChangeDialog = (props: any) => {
  const [contractors, setContractors] = useState<ContractorDto[]>([]);
  const [contractorSelected, setContractorSelected] = useState();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const phaseType = props.selectedJobPhase?.phaseType;

  const onChangeContractor = (event: any) => {
    setContractorSelected(event.target.value);
  };

  const getContractors = async () => {
    const contractorData: GetListResult<ContractorDto> =
      await dataProvider.getList('contractor', {
        filter: {
          ...getContractorFilter(phaseType, props.regionId, props.job),
          $join: [{ field: 'contractorRegions' }],
        },
        pagination: { page: 1, perPage: PHASE_CONTRACTOR_LIST_LIMIT },
        sort: { field: 'name', order: 'ASC' },
      });
    if (contractorData.data) {
      setContractors(contractorData.data);
    } else {
      throw new Error('Job phases not found');
    }
  };

  useEffect(() => {
    if (props.selectedJobPhase) {
      getContractors();
    }
  }, [props.selectedJobPhase]);

  const onConfirmContractor = async () => {
    try {
      const isBump = BUMP_PHASES.includes(phaseType);

      if (isBump && props.job.job.reqBumpoutTimesheet) {
        const jobPhaseBump = await dataProvider.getList<JobPhaseBumpDto>(
          'job-phase-bump',
          {
            filter: {
              jobPhaseId: props.selectedJobPhase.id,
            },
            pagination: { page: 1, perPage: 200 },
            sort: { field: 'id', order: 'ASC' },
          },
        );

        const params = {
          id: jobPhaseBump.data[0]?.id,
          data: {
            contractorId: contractorSelected,
          },
          previousData: props.selectedJobPhase,
        };
        await dataProvider.update('job-phase-bump', params);
      } else {
        const params = {
          id: props.selectedJobPhase.id,
          data: {
            contractorId: contractorSelected,
          },
          previousData: props.selectedJobPhase,
        };
        await dataProvider.update('job-phase', params);
      }

      notify('Contractor Assigned!');
      props.handleClose();
      props.refreshJobPhases();
    } catch (error) {
      notify(error.message, 'warning');
    }
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.handleClose}
      aria-labelledby="alert-dialog-title-"
      aria-describedby="alert-dialog-description9"
    >
      <DialogTitle id="alert-dialog-ti9tle">Change Contractor</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-desc9ription">
          <Box display="flex">
            <Select
              onChange={onChangeContractor}
              labelId={`custom-reference-contractor`}
              id={`reference-contractor`}
              fullWidth
              value={contractorSelected}
              disabled={props.isLaborCoordinator}
            >
              {contractors.map((item: ContractorDto) => (
                <MenuItem
                  key={item.id}
                  value={item.id}
                  data-item={item.id}
                  data-name={item.name}
                >
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </DialogContentText>
        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button onClick={props.handleClose} color="primary">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onConfirmContractor}
            color="primary"
            disabled={!contractorSelected}
          >
            Save
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ContractorChangeDialog;